import styles from './styles.module.scss';
import {Input, Select, Form, Typography, Spin, Divider, Tooltip} from 'antd';
import classNames from 'classnames';
import {useStore} from '@/store/root-store';
import React, {Fragment, useEffect, useRef, useState} from 'react';
import {Modal, CommonInput} from '@/components/common-components';
import {Button as V2Button, GrayButton} from '@/components/common-components/v2/Button';
import {LoadingOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {observer} from 'mobx-react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck, faCircleCheck} from '@fortawesome/pro-regular-svg-icons';
import styled from 'styled-components';
import {Search} from 'react-feather';
import {toJS} from 'mobx';
import {timeNotification} from '@/utils/notification-timer';
import {faFolder, faSpinner} from '@fortawesome/pro-light-svg-icons';
import {faCaretDown} from '@fortawesome/pro-solid-svg-icons';


interface Props {
  projectName: string;
  pageUuid?: string;
  selectedKeywords?:any;
  minifiedVersion?: boolean;
  disabled?: boolean;
}

export const ProjectsSelector = observer(({projectName, pageUuid, selectedKeywords=[], minifiedVersion, disabled}: Props) => {
  const [showAddNewModal, setShowAddNewModal] = useState(false);
  const [modalSelectedItem, setModalSelectedItem] = useState(projectName);

  const {
    contentOptimizer: {
      pages: {
        projects,
        addArticleToProject,
        createProjectsByName,
        projectsLoading,
        loadPages,
        setUpdatingPageUuid,
        updatingPageUuid,
        bulkUpdateData,
        activeProjectId,
        currentProject,
      },
      currentPage: {loadPage, setUpdatingFolder, updatingFolder},
    },
  } = useStore('');
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectDomain, setNewProjectDomain] = useState('');
  const [projectsData, setProjectsData] = useState<any[]>([]);
  const [form] = Form.useForm();
  const {Title, Text} = Typography;
  const [newProjectSubmitDisabled, setNewProjectSubmitDisabled] = useState(true);
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  const {t} = useTranslation('common');
  const antIcon = <LoadingOutlined style={{fontSize: 24}} spin />;
  const {Option}= Select;

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const createProjectHandler = async () => {
    if (!newProjectTitle || !newProjectDomain || projects?.find(project => project.name === newProjectTitle)) return;

    try {
      await createProjectsByName(newProjectTitle, false, newProjectDomain);
      await loadPages(false, activeProjectId);
      setShowAddNewModal(false);
      setNewProjectTitle('');
      setNewProjectDomain('');
      form.resetFields();
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const validateUrl = (_, value) => {
    const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/;
    const trimmedValue = value?.trim();
    if (trimmedValue.includes(' ')) {
      return Promise.reject(new Error('URL cannot contain spaces'));
    }


    if ( !value || urlPattern.test(trimmedValue)) {
      return Promise.resolve();
    }

    return Promise.reject(new Error('URL must be valid'));
  };

  const onCloseModal = () => {
    setNewProjectTitle('');
    setNewProjectDomain('');
    setShowAddNewModal(false);
    form.resetFields();
    setNewProjectSubmitDisabled(true);
  };

  const addNewModal = (
    <Modal visible={showAddNewModal} onClose={() => onCloseModal()}>
      <div className={styles.deleteModalContent}>
        <Title className={styles.deleteModalTitle} level={4}>Add SCA Project</Title>
      </div>
      <div className={styles.deleteModalFooter}>
        <Form
          form={form}
          onFieldsChange={() => {
            const errors = form.getFieldsError();
            const fieldsWithErrors = errors.filter(({errors}) => errors.length > 0)?.map(item => item?.name?.[0]);
            if (fieldsWithErrors?.length) {
              setNewProjectSubmitDisabled(true);
            } else {
              setNewProjectSubmitDisabled(false);
            }
          }}
          onFinish={createProjectHandler}>
          <Text style={{fontSize: '12px'}} type='secondary'>{'Project name'}</Text>
          <Form.Item
            name='projectInput'
            style={{marginBottom: 10}}
            rules={[
              {
                required: true,
                message: 'Project name is required',
              },
              () => ({
                validator(_, value) {
                  if (projects?.find(project => project.name === value)) {
                    return Promise.reject(new Error('Project name already exists'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <CommonInput
              name='1'
              onChange={e => {
                setNewProjectTitle(e.target.value);
              }
              }
              inputType='text'
              variant='light'
              placeholder={'Name or website URL...'}/>

          </Form.Item>
          <Text style={{fontSize: '12px'}} type='secondary'>{'Domain'}</Text>
          <Form.Item
            name='domain'
            validateFirst
            rules={[
              {
                required: true,
                message: 'Domain is required',
              },
              {
                validator: validateUrl,
              },
            ]}
          >
            <CommonInput
              name='1'
              inputType='text'
              variant='light'
              onChange={e => {
                setNewProjectDomain(e.target.value);
              }}
              placeholder={'Domain'}/>

          </Form.Item>
          <Form.Item className={styles.editUrlBtnContainer} style={{marginTop: 16}}>
            <GrayButton
              color='white'
              variant='border'
              className={styles.cancelImportUrlBtn}
              style={{marginRight: 10, marginLeft: 0}}
              onClick={e => {
                e.preventDefault();
                onCloseModal();
              }}
              disabled={projectsLoading}
            >
              {t('cancel')}
            </GrayButton>
            <V2Button
              color='purple'
              htmlType='submit'
              variant='solid'
              className={styles.importUrlButton}
              disabled={projectsLoading || newProjectSubmitDisabled}
            >
              {projectsLoading ? <Spin indicator={antIcon} /> : 'Create Project'}
            </V2Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );


  const onSearchHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setProjectsData(toJS(projects).filter(option => option?.name?.toLowerCase().includes(e.target.value.toLowerCase())));
    } else {
      setProjectsData(toJS(projects)?.slice());
    }
  };

  useEffect(() => {
    setProjectsData(projects);
  }, [projects]);

  useEffect(() => {
    setModalSelectedItem(projectName);
  }, [projectName]);

  const defaultProjects = ['Total Articles', 'Uncategorized Articles'];

  const onOptionChange = async (option, uuid, updatingUuid) => {
    setUpdatingFolder(true);
    const currentSelection = modalSelectedItem;
    const selectedItem = toJS(projects)?.find(elem => elem.id === option);
    if (selectedKeywords?.length > 0) {
      const timer = setTimeout(async () => {
        try {
          setModalSelectedItem(selectedItem.name);
          await bulkUpdateData(selectedKeywords, '', '', [], selectedItem?.name, false);
          await loadPages(false, activeProjectId);
        } finally {
          setUpdatingFolder(false);
          timeNotification(
            '#1FAC47',
            faCircleCheck,
            5,
            `${selectedKeywords.length} ${selectedKeywords.length > 1 ? 'articles' : 'article'} moved to project "${selectedItem.name}"`,
            () => clearTimeout(timer),
          );
        }
      }, 5000);
    } else {
      const formatUpdateArr = [...updatingUuid];
      if (selectedItem && selectedItem[0] && selectedItem[0]?.name) {
        setModalSelectedItem(selectedItem[0].name);
      }
      if (!formatUpdateArr.includes(uuid)) {
        formatUpdateArr.push(uuid);
      }
      setUpdatingPageUuid(formatUpdateArr);
      const timer = setTimeout(async () => {
        try {
          if (updatingUuid.includes(uuid)) {
            const cleanedArr = updatingUuid.filter(elem => elem !== uuid);
            setUpdatingPageUuid(cleanedArr);
            await addArticleToProject(option, uuid);
            let projectId = null;
            if (currentProject && activeProjectId && !defaultProjects.includes(currentProject)) {
              projectId = activeProjectId;
            }
            await loadPages(false, projectId, true);
            await loadPage(uuid as string, false);
          } else {
            setModalSelectedItem(projectName);
          }
        } finally {
          setModalSelectedItem(currentSelection);
          setUpdatingFolder(false);
          timeNotification(
            '#1FAC47',
            faCircleCheck,
            5,
            `Article moved to project "${selectedItem?.name}"`,
            () => {
              clearTimeout(timer);
            },
          );
        }
      }, 5000);
    }
  };

  const addEllipsis = (str, n)=>{
    return (str.length > n) ? str.slice(0, n-1) + '....' : str;
  };

  const selectRef = useRef(null);
  return (<MainDiv minified={minifiedVersion} className='projectSelectorWrapper'>
    {addNewModal}
    <Tooltip title={updatingFolder ? 'Updating folder' : modalSelectedItem ? modalSelectedItem : 'Select Project'} placement='top' overlayStyle={!minifiedVersion ? {display: 'none'} : {}}>
      <Select
        disabled={disabled || updatingFolder}
        style={{opacity: disabled ? .5 : 1, maxWidth: screenWidth < 700 && '146px'}}
        placement={minifiedVersion ? 'bottomLeft' : 'bottomRight'}
        dropdownClassName={styles.projectsDropdown}
        placeholder={!minifiedVersion ? projectName ? projectName : 'Select Project' : <></>}
        value={modalSelectedItem}
        ref={selectRef}
        suffixIcon={minifiedVersion ? <Tooltip title={updatingFolder ? 'Updating folder' : projectName ? projectName : 'Select Project'} placement='top'>
          <div style={{display: 'flex', alignItems: 'center'}}><FontAwesomeIcon spin={updatingFolder} icon={updatingFolder ? faSpinner : faFolder} color={'#4E5156'} fontSize={14}/><FontAwesomeIcon icon={faCaretDown} style={{marginLeft: 5}} color={'#4E5156'}/></div>
        </Tooltip> :
          <FontAwesomeIcon icon={faCaretDown} color={'#4E5156'}/>}
        onChange={option => onOptionChange(option, pageUuid, updatingPageUuid)}
        dropdownRender={menu => (
          <Fragment>
            <div onClick={e=> e.stopPropagation()}
              className={styles.topDiv}>
              <Input className={styles.SearchField}
                placeholder={t('Search...')}
                onChange={onSearchHandler}
                prefix={<Search width={15}/>}
                bordered={false}
              />
            </div>
            <Divider className={styles.divider} />
            {menu}
            <div
              className={styles.Option}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                setShowAddNewModal(true);
              }}>
              <Divider className={styles.divider} style={{marginBottom: 10}}/>
              <AddProjectBtn onClick={() => {
                selectRef?.current?.blur();
              }}>+ Add new Project </AddProjectBtn>
            </div>
          </Fragment>
        )}>
        {
          projectsData?.map((item, idx) => {
            if (item.id !== null) {
              return (
                <Option
                  className={classNames(styles.Option, item.name == modalSelectedItem ? styles.activeOption : null)}
                  key={idx}
                  value={item.id}
                  onClick={e => e.stopPropagation()}
                  disabled={item.name == modalSelectedItem}>
                  <div style={{display: 'flex'}}>
                    {addEllipsis(item.name || '', screenWidth < 700 ? 14 : 26)}
                    {item.name == modalSelectedItem && <FontAwesomeIcon icon={faCheck} style={{marginLeft: 'auto'}}/>}
                  </div>

                </Option>
              );
            }
          })
        }
      </Select>
    </Tooltip>


  </MainDiv>);
});

const AddProjectBtn = styled.div`
  color: #2D6CCA;
  cursor: pointer;
  margin-left: 10px;
  margin-bottom: 10px;
  font-size: 12px;
`;


const MainDiv= styled.div<{minified?: boolean}>`
  margin-right: 5px;
  border-radius: 4px;
  padding: 3px 5px;
  position: relative;
  ${p => p.minified ? 'width: 40px' : ''};
  @media screen and (max-width: 770px) {
    padding-left: 0;
  }

  .ant-select {
    &:hover {
      .ant-select-selector {
        ${p => p.minified ? 'border-color: transparent !important' : ''};
      }
     
    }
    .ant-select-focused {
      ${p => p.minified ? 'border-color: transparent !important' : ''};
      ${p => p.minified ? 'box-shadow: 0px !important' : ''};

      .ant-select-selector {
        ${p => p.minified ? 'box-shadow: 0px !important' : ''};
        ${p => p.minified ? 'border-color: transparent !important' : ''};
      }
    }
    .ant-select-selector {
      border: ${p => p.minified ? '1px solid transparent' : '1px solid transparent'};
      ${p => p.minified ? 'width: 37px' : ''};
      ${p => p.minified ? 'padding: 0' : ''};
      background:  ${p => p.minified ? 'transparent !important' : '#F7F7F8 !important'};
      border-radius: 5px !important;
      height: 30px !important;

      .ant-select-selection-item {
        display: ${p => p.minified ? 'none' : 'block'};
      }
    }

    .ant-select-selection-placeholder {
      color: #4E5156 !important;
    }
  }

`;

